import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import api from '../../lib/api'

interface KleisImportResult {
  materialsCreated: number
  materialsUpdated: number
  serialsCreated: number
  serialsSkipped: number
  errors: Array<{ row: number; code: string; message: string }>
}

function ImportDropzone({
  selectedFile,
  onDrop,
}: {
  selectedFile: File | null
  onDrop: (files: File[]) => void
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  })

  return (
    <Card noPadding>
      <div
        {...getRootProps()}
        className={`
          flex flex-col items-center justify-center gap-3 p-10 rounded-lg border-2 border-dashed cursor-pointer transition-colors
          ${isDragActive
            ? 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_5%,transparent)]'
            : 'border-[var(--fc-border)] hover:border-[var(--fc-primary)] hover:bg-[var(--fc-surface-overlay)]'
          }
        `}
      >
        <input {...getInputProps()} aria-label="Carica file Excel magazzino" />
        {selectedFile ? (
          <>
            <FileSpreadsheet className="h-12 w-12 text-[var(--fc-primary)]" aria-hidden="true" />
            <div className="text-center">
              <p className="font-medium text-[var(--fc-foreground)]">{selectedFile.name}</p>
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                {(selectedFile.size / 1024).toFixed(1)} KB — Clicca per cambiare file
              </p>
            </div>
          </>
        ) : (
          <>
            <Upload
              className={`h-12 w-12 ${isDragActive ? 'text-[var(--fc-primary)]' : 'text-[var(--fc-foreground-muted)]'}`}
              aria-hidden="true"
            />
            <div className="text-center">
              <p className="font-medium text-[var(--fc-foreground)]">
                {isDragActive ? 'Rilascia il file qui' : 'Trascina il file Excel oppure clicca'}
              </p>
              <p className="text-sm text-[var(--fc-foreground-muted)]">Supporta .xlsx e .xls fino a 10 MB</p>
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

function ErrorTable({ errors }: { errors: Array<{ row: number; code: string; message: string }> }) {
  if (errors.length === 0) return null
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-sm font-medium text-[var(--fc-danger)]">
        <AlertTriangle className="h-4 w-4" />
        Errori per riga ({errors.length})
      </div>
      <div className="max-h-60 overflow-y-auto rounded border border-[var(--fc-border)]">
        <table className="w-full text-xs">
          <thead className="bg-[var(--fc-surface-overlay)]">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Riga</th>
              <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Codice</th>
              <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Errore</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((err, i) => (
              <tr key={i} className="border-t border-[var(--fc-border)]">
                <td className="px-3 py-1.5 text-[var(--fc-foreground-muted)]">{err.row}</td>
                <td className="px-3 py-1.5 font-mono text-[var(--fc-foreground)]">{err.code || '—'}</td>
                <td className="px-3 py-1.5 text-[var(--fc-danger)]">{err.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function MaterialsImportPage() {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<KleisImportResult | null>(null)
  const [warehouseId, setWarehouseId] = useState<string>('')

  const { data: warehouses = [] } = useQuery<{ id: string; name: string; type: string }[]>({
    queryKey: ['warehouses-peripheral'],
    queryFn: () => api.get('/warehouse/locations').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  })
  const peripheralWarehouses = warehouses.filter((w) => w.type === 'peripheral')

  const mutation = useMutation({
    mutationFn: async ({ file, warehouseId }: { file: File; warehouseId: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('warehouseId', warehouseId)
      const res = await api.post<KleisImportResult>('/materials/import/kleis', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: (data) => setResult(data),
  })

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/materials/import/template', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'fieldcore-template-magazzino.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      // silenzioso
    }
  }

  const hasSuccess = result && (result.materialsCreated > 0 || result.materialsUpdated > 0 || result.serialsCreated > 0)

  return (
    <div className="space-y-4 max-w-3xl">
      <PageHeader
        title="Importa materiali da Excel"
        breadcrumb={[
          { label: 'Magazzino', href: '/warehouse' },
          { label: 'Materiali', href: '/warehouse/materials' },
          { label: 'Importa' },
        ]}
      />

      {/* Info formato */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)]">Formato file richiesto</h3>
            <p className="text-sm text-[var(--fc-foreground-muted)]">
              Carica il file Excel magazzino con le schede per ogni location. Il sistema creerà
              automaticamente i materiali e traccerà ogni numero seriale.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {["SOCIETA'", "QUANTITA'", 'TIPOLOGIA', 'P/N', 'S/N', 'NOTE', 'UBICAZIONE'].map((col) => (
                <span
                  key={col}
                  className="inline-block rounded bg-[var(--fc-surface-overlay)] px-2 py-0.5 font-mono text-xs text-[var(--fc-foreground)]"
                >
                  {col}
                </span>
              ))}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => void handleDownloadTemplate()}
            className="shrink-0"
          >
            Scarica template
          </Button>
        </div>
      </Card>

      {/* Selettore magazzino */}
      <Card>
        <div className="space-y-1">
          <label className="text-sm font-medium text-[var(--fc-foreground)]">
            Magazzino di destinazione <span className="text-[var(--fc-danger)]">*</span>
          </label>
          <select
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-primary)]"
          >
            <option value="">— Seleziona magazzino —</option>
            {peripheralWarehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Dropzone */}
      <ImportDropzone
        selectedFile={file}
        onDrop={(files) => { setFile(files[0] ?? null); setResult(null) }}
      />

      {/* Azioni */}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate('/warehouse/materials')}>
          Annulla
        </Button>
        <Button
          onClick={() => { if (file && warehouseId) mutation.mutate({ file, warehouseId }) }}
          disabled={!file || !warehouseId || mutation.isPending}
          leftIcon={<Upload className="h-4 w-4" />}
        >
          {mutation.isPending ? 'Importazione in corso...' : 'Avvia importazione'}
        </Button>
      </div>

      {mutation.isError && (
        <Alert variant="error">
          Errore durante l&apos;importazione. Verificare il file e riprovare.
        </Alert>
      )}

      {/* Risultati */}
      {result && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Risultati importazione</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 mb-4">
            {[
              { label: 'Materiali creati', value: result.materialsCreated, color: 'text-[var(--fc-success)]' },
              { label: 'Materiali aggiornati', value: result.materialsUpdated, color: 'text-[var(--fc-primary)]' },
              { label: 'Seriali creati', value: result.serialsCreated, color: 'text-[var(--fc-success)]' },
              { label: 'Seriali saltati', value: result.serialsSkipped, color: 'text-[var(--fc-foreground-muted)]' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded border border-[var(--fc-border)] p-2 text-center">
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-[var(--fc-foreground-muted)]">{label}</p>
              </div>
            ))}
          </div>
          <ErrorTable errors={result.errors} />
          {hasSuccess ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--fc-success)]">
              <CheckCircle className="h-4 w-4" />
              Importazione completata con successo.
            </div>
          ) : result.errors.length > 0 ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--fc-danger)]">
              <XCircle className="h-4 w-4" />
              Nessun record importato. Correggere gli errori e riprovare.
            </div>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setResult(null); setFile(null) }}>
              Importa altro file
            </Button>
            {hasSuccess && (
              <Button onClick={() => navigate('/warehouse/materials')}>
                Vai ai materiali
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
